import React, { useRef, useState } from "react"
import { ReactSVG } from "react-svg"
import { MainBlocksFieldsFragment } from "../../../../graphql-types"
import { infoTrafficBlockPicto, itineraryBlockPicto, timesheet, triangle } from "../../../../static/images"
import * as styles from "./main-blocks-mobile-tabs.module.scss"

type RenderProps = {
  mainBlocks: MainBlocksFieldsFragment[]
  onActiveTabChange: (activeBlockId: string) => void
}

type BlockPresentation = {
  title?: string | null
  widgetType?: string | null
  image?: { url?: string | null } | null
}

type BlockPicto = {
  src: string
  isPng: boolean
}

const BLOCK_TYPE_IMAGES: Partial<Record<string, string>> = {
  DatoCmsSchedulesSearchBlock: timesheet,
  DatoCmsTrafficInfoBlock: infoTrafficBlockPicto,
  DatoCmsInstantSystemWidgetBlock: infoTrafficBlockPicto,
}

export const mainBlockTabId = (blockId: string) => `main-block-tab-${blockId}`
export const mainBlockPanelId = (blockId: string) => `main-block-panel-${blockId}`

const getBlockPicto = (block: MainBlocksFieldsFragment): BlockPicto | null => {
  const presentation = block as BlockPresentation
  const typeImage = BLOCK_TYPE_IMAGES[block.__typename]

  if (typeImage) {
    return { src: typeImage, isPng: false }
  }
  if (block.__typename === "DatoCmsViaTransitWidgetBlock") {
    if (presentation.widgetType === "Info trafic") {
      return { src: infoTrafficBlockPicto, isPng: false }
    }
    if (presentation.widgetType === "Itinéraire") {
      return { src: itineraryBlockPicto, isPng: true }
    }
  }
  if (block.__typename === "DatoCmsCustomBlockExternalPage" && presentation.image?.url) {
    return { src: presentation.image.url, isPng: false }
  }
  return null
}

const MainBlocksMobileTabs: React.FC<RenderProps> = ({ mainBlocks, onActiveTabChange }) => {
  const [activeTab, setActiveTab] = useState(mainBlocks[1]?.id)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectTab = (blockId: string, focusIndex?: number) => {
    setActiveTab(blockId)
    onActiveTabChange(blockId)
    if (focusIndex !== undefined) {
      tabRefs.current[focusIndex]?.focus()
    }
  }

  const focusTabAt = (index: number) => {
    const nextIndex = (index + mainBlocks.length) % mainBlocks.length
    selectTab(mainBlocks[nextIndex].id, nextIndex)
  }

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault()
        focusTabAt(index + 1)
        break
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault()
        focusTabAt(index - 1)
        break
      case "Home":
        event.preventDefault()
        focusTabAt(0)
        break
      case "End":
        event.preventDefault()
        focusTabAt(mainBlocks.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div className={styles.mainBlocksTabs} role="tablist">
      {mainBlocks.map((block, index) => {
        const isSelected = block.id === activeTab
        const presentation = block as BlockPresentation
        const picto = getBlockPicto(block)

        return (
          <button
            key={block.id}
            ref={element => {
              tabRefs.current[index] = element
            }}
            type="button"
            role="tab"
            id={mainBlockTabId(block.id)}
            aria-selected={isSelected}
            aria-controls={mainBlockPanelId(block.id)}
            tabIndex={isSelected ? 0 : -1}
            className={`${styles.mainBlockHeader} ${isSelected ? styles.active : ""}`}
            onClick={() => selectTab(block.id)}
            onKeyDown={event => handleTabKeyDown(event, index)}
          >
            {picto &&
              (picto.isPng ? (
                <img src={picto.src} className={styles.blockHeaderPicto} alt="" aria-hidden="true" />
              ) : (
                <ReactSVG
                  src={picto.src}
                  className={styles.blockHeaderPicto}
                  beforeInjection={svg => svg.setAttribute("aria-hidden", "true")}
                />
              ))}
            <h2 className={styles.tabTitle}>{presentation.title || presentation.widgetType}</h2>
            <ReactSVG
              src={triangle}
              className={styles.triangle}
              beforeInjection={svg => {
                svg.setAttribute("aria-hidden", "true")
              }}
            />
          </button>
        )
      })}
    </div>
  )
}

export default MainBlocksMobileTabs
